import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CheckoutSummary from '../components/CheckoutSummary';
import "../style/AppPages.css";
import { useCart } from '../contexts/CartContext';
import Loading from '../components/Loading';
import { fetchOrderFromBD } from '../controllers/AccountConnectionLayer';
import useViewport from '../hooks/useViewport';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../contexts/CurrencyContext';

const OrderCompletePage = () => {
    const { isVeryMobileView } = useViewport();
    const [searchParams] = useSearchParams();
    const [orderDetails, setOrderDetails] = useState(null);
    const [orderDetailsBD, setOrderDetailsBD] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [checkoutCartData, setCheckoutCartData] = useState(null);
    const { clearCart } = useCart();
    const { t } = useTranslation();
    const [couponAmount, setCouponAmount] = useState(null);
    const [giftcardAmount, setGiftcardAmount] = useState(null);
    const [freeShipping, setFreeShipping] = useState(false);
    const { formatPrice, currencySymbol, getCurrencyDecimalsByCode } = useCurrency();

    useEffect(() => {
        const fetchOrderDetails = async () => {
            const paymentIntentId = searchParams.get('payment_intent');

            if (!paymentIntentId) {
                setError('No payment intent found.');
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}stripe/payment-intent/${paymentIntentId}`);
                if (!response.ok) {
                    throw new Error('Failed to fetch payment details');
                }

                const data = await response.json();
                
                setOrderDetails(data);
              
                if (data.metadata.coupon.split(" - ")[1] !== "null") {
                    setCouponAmount(data.metadata.coupon.split(" - ")[1]);
                }

                if (data.metadata.cardAmount !== "0") {
                    setGiftcardAmount(data.metadata.cardAmount);
                }

                if (data.metadata.freeShipping) {
                    setFreeShipping(data.metadata.freeShipping === "true" ? true : false);
                }
                    
                const respondeBD = await fetchOrderFromBD(data.id, t);

                setOrderDetailsBD(respondeBD.data);
            } catch (error) {
                setError(error.message);
            } finally {
                clearCart();
                setLoading(false);
            }
        };

        fetchOrderDetails();

        const savedData = localStorage.getItem('checkoutCartData');

        setCheckoutCartData(JSON.parse(savedData));

    }, [searchParams]);

    if (error) return <p>Error: {error}</p>;

    return (
        <>
        <Loading isVisible={loading} />
            {!loading && (
                <div className="order-complete page-padding">
                    <div className="account-update-container make-smaller">
                        <h1>{t("complete.title")}</h1>

                        <div class="small-horizontal-container">
                            <div class="horizontal-purple"></div>
                        </div>

                        <h2 className="big-text">{t("complete.recieptMessage")}</h2>
                        {orderDetails ? (
                            <div className="order-confirmation-infos">
                                <div className="address-update-section padding-section-confirm confirm-row-section confirm-wider flex-container">
                                    <div className="flex-container">
                                        <div className="upper-section-divison">
                                            <div className="wrapper-confirm">
                                                <h2 className="title-padding-confirm">Order Summary</h2>
                                                <div className="left-padded-horizontal-container">
                                                    <div className="small-horizontal-fit"></div>
                                                </div>
                                            </div>
                                            <p>{t("history.numberLabel")}: #{orderDetailsBD.order_number}</p>
                                            <p>{t("history.statusLabel")}: {orderDetailsBD.status}</p>
                                            {orderDetailsBD.tracking && (
                                                <p>{t("complete.trackingLabel")} :{orderDetailsBD.tracking}</p>
                                            )}
                                        </div>

                                        <div className="upper-section-divison">
                                            <h3>{t("complete.billingLabel")}</h3>
                                            <p>{t("update.nameLabel")}: {orderDetails.shipping.name}</p>
                                            <p>{t("update.emailLabel")}: {orderDetails.receipt_email}</p>
                                        </div>

                                        {/* Conditionally render billing address section */}
                                        {(() => {
                                            const {
                                                billing_line1,
                                                billing_line2,
                                                billing_city,
                                                billing_state,
                                                billing_postal_code
                                            } = orderDetails.metadata;

                                            const isValid = val => val && val !== "undefined" && val !== "null";

                                            const shouldDisplayBilling =
                                                isValid(billing_line1) ||
                                                isValid(billing_line2) ||
                                                isValid(billing_city) ||
                                                isValid(billing_state) ||
                                                isValid(billing_postal_code);

                                            if (!shouldDisplayBilling) return null;

                                            return (
                                                <div className="upper-section-divison">
                                                    <h3>Billing Address</h3>

                                                    {isValid(billing_line1) && (
                                                        <p>
                                                            {isValid(billing_line2)
                                                                ? `${billing_line2} - ${billing_line1}`
                                                                : billing_line1}
                                                        </p>
                                                    )}

                                                    {isValid(billing_city) && isValid(billing_state) && (
                                                        <p>{billing_city}, {billing_state}</p>
                                                    )}

                                                    {isValid(billing_postal_code) && <p>{billing_postal_code}</p>}
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    <div className="flex-container">
                                        <div className="upper-section-divison">
                                            <h3>{t("complete.shippingLabel")}</h3>
                                            {orderDetails.shipping.address.line2 ? (
                                                <p>{orderDetails.shipping.address.line2}-{orderDetails.shipping.address.line1}</p>
                                            ) : (
                                                <p>{orderDetails.shipping.address.line1}</p>
                                            )}
                                            <p>{orderDetails.shipping.address.city}, {orderDetails.shipping.address.state}</p>
                                            <p>{orderDetails.shipping.address.postal_code}</p>
                                        </div>

                                        <div className="upper-section-divison">
                                            <h3>{t("complete.paymentLabel")}</h3>
                                            <p>{t("complete.paymentMethod")}: {orderDetails.payment_method_types[0]}</p>
                                            <p>{t("complete.paymentLabel")}: {currencySymbol}{formatPrice(
                                                getCurrencyDecimalsByCode(orderDetails.currency)
                                                    ? orderDetails.amount / 100
                                                    : orderDetails.amount,
                                                orderDetails.currency
                                            )} {orderDetails.currency.toUpperCase()}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="address-update-section padding-section-confirm confirm-wider">
                                    <CheckoutSummary asyncCartItems={checkoutCartData.asyncCartItems} taxes={checkoutCartData.taxes} selectedShippingOption={checkoutCartData.selectedShippingOption} totalCost={checkoutCartData.totalCost} isTinyMobileView={isVeryMobileView} couponAmount={couponAmount} giftcardAmount={giftcardAmount} freeShipping={freeShipping}/>
                                </div>
                            </div>
                        ) : (
                            <p>{t("complete.noneLabel")}</p>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default OrderCompletePage;
